import { z } from "zod";

// The controlled vocabularies of the Solar PV sizing module (EOS-03d), copied
// verbatim from the backend so a value the API can send is never rejected
// here. Sources: backend/app/domain/electrical/sources/pv_results.py
// (PVSizingStatus, PVSizingWarningCode, NOT_ESTABLISHED_WARNING_CODES,
// NO_SOLUTION_WARNING_CODES) and .../sources/pv_models.py (PVSystemType,
// PVPhaseConfiguration, PVInverterRedundancyMode, PVBatteryConfiguration).
// pvSizingContract.test.ts pins each list.
//
// The operating scenario is the same enum the load module uses, so it is reused
// from loadDemandContract rather than written out a second time.

export const pvSizingStatusSchema = z.enum([
  "VALID",
  "WARNING",
  "REVIEW_REQUIRED",
  "NO_SOLUTION",
]);

export const pvSizingWarningCodeSchema = z.enum([
  "DERATING_APPLIED",
  "PV_ENERGY_YIELD_NOT_EVALUATED",
  "PV_BATTERY_NOT_EVALUATED",
  "EXPORT_LIMIT_APPLIED",
  "DG_COORDINATION_REQUIRED",
  "NO_STANDARD_INVERTER_RATING",
  "STRING_LENGTH_WINDOW_EMPTY",
  "MPPT_INPUT_LIMIT_EXCEEDED",
  "GROWTH_FACTOR_NOT_ESTABLISHED",
  "DESIGN_MARGIN_NOT_ESTABLISHED",
  "AMBIENT_DERATING_NOT_ESTABLISHED",
  "ALTITUDE_DERATING_NOT_ESTABLISHED",
  "AC_EFFICIENCY_NOT_ESTABLISHED",
]);

export const pvSystemTypeSchema = z.enum(["GRID_TIED", "HYBRID", "OFF_GRID"]);

export const pvPhaseConfigurationSchema = z.enum(["SINGLE_PHASE", "THREE_PHASE"]);

export const pvInverterRedundancyModeSchema = z.enum([
  "NONE",
  "N_PLUS_1",
  "TWO_N",
]);

export const pvBatteryConfigurationSchema = z.enum(["NONE", "DC_COUPLED", "AC_COUPLED"]);

/** The five warnings that make a result REVIEW_REQUIRED rather than WARNING (A19 (h)). */
export const NOT_ESTABLISHED_WARNING_CODES = [
  "GROWTH_FACTOR_NOT_ESTABLISHED",
  "DESIGN_MARGIN_NOT_ESTABLISHED",
  "AMBIENT_DERATING_NOT_ESTABLISHED",
  "ALTITUDE_DERATING_NOT_ESTABLISHED",
  "AC_EFFICIENCY_NOT_ESTABLISHED",
] as const;

/** The three reasons a study has nothing to build with (A19 (l)). */
export const NO_SOLUTION_WARNING_CODES = [
  "NO_STANDARD_INVERTER_RATING",
  "STRING_LENGTH_WINDOW_EMPTY",
  "MPPT_INPUT_LIMIT_EXCEEDED",
] as const;

/**
 * Carried by every PV result (A19 (b), GAP-019): the annual energy yield is not
 * evaluated. It gives WARNING, never REVIEW_REQUIRED.
 */
export const PV_ENERGY_YIELD_NOT_EVALUATED = "PV_ENERGY_YIELD_NOT_EVALUATED";

/** The choice the engineer must make; the backend imposes no default (A19 (g)). */
export const REQUIRED_PV_CHOICES = ["redundancy_mode"] as const;

/**
 * Descriptors: optional, no default, echoed in the result, no effect on any number
 * (A19 (j)).
 */
export const PV_DESCRIPTORS = [
  "system_type",
  "phase_configuration",
  "battery_configuration",
  "scenario",
] as const;

export type PVSizingStatus = z.infer<typeof pvSizingStatusSchema>;
export type PVSizingWarningCode = z.infer<typeof pvSizingWarningCodeSchema>;
export type PVSystemType = z.infer<typeof pvSystemTypeSchema>;
export type PVPhaseConfiguration = z.infer<typeof pvPhaseConfigurationSchema>;
export type PVInverterRedundancyMode = z.infer<typeof pvInverterRedundancyModeSchema>;
export type PVBatteryConfiguration = z.infer<typeof pvBatteryConfigurationSchema>;
